import { APP_CONFIG } from '@/constants/app';
import { tokenManager } from '@/utils/token-manager';
import axios from 'axios';
import {
  Document,
  DocumentUploadResponse,
  DocumentsListResponse,
  DocumentsQueryParams,
  UploadedDocument,
  TextDocumentCreate,
  CredentialDocumentCreate,
  DocumentCredentialsResponse,
} from '../types/documents.types';

const documentsApi = axios.create({
  baseURL: APP_CONFIG.api.baseUrl + '/documents',
  // Content-Type sera défini automatiquement par Axios selon le type de requête
  // (application/json pour les objets, multipart/form-data pour FormData)
});

// Setup automatic token refresh interceptors
tokenManager.setupInterceptors(documentsApi);

export const documentsService = {
  /**
   * Get documents list avec filters et pagination
   * GET /documents
   */
  getDocuments: async (
    params: DocumentsQueryParams
  ): Promise<DocumentsListResponse> => {
    const response = await documentsApi.get<DocumentsListResponse>('/', {
      params,
    });
    // Le backend retourne directement DocumentsListResponse (pas d'enveloppe)
    return response.data;
  },

  /**
   * Get single document by UUID
   * GET /documents/:uuid
   */
  getDocument: async (uuid: string): Promise<Document> => {
    const response = await documentsApi.get<{ data: Document }>(`/${uuid}`);
    // Le backend enveloppe la réponse dans { data: {...}, meta: {...} }
    return response.data.data;
  },

  /**
   * Upload single or multiple documents
   * POST /documents/upload (multipart/form-data)
   */
  uploadDocuments: async (
    documents: UploadedDocument[]
  ): Promise<DocumentUploadResponse> => {
    const formData = new FormData();

    // Ajouter les fichiers
    documents.forEach(doc => {
      formData.append('files[]', doc.file);
    });

    // Ajouter les métadonnées (JSON stringifié)
    const metadata = documents.map(doc => ({
      name: doc.name,
      description: doc.description,
      category: doc.category,
      tags: doc.tags,
      context: doc.context,
      contextUuid: doc.contextUuid,
    }));
    formData.append('metadata', JSON.stringify(metadata));

    const response = await documentsApi.post<{ data: DocumentUploadResponse }>(
      '/upload',
      formData
    );
    // Le backend enveloppe la réponse dans { data: {...}, meta: {...} }
    return response.data.data;
  },

  /**
   * Delete document
   * DELETE /documents/:uuid
   */
  deleteDocument: async (uuid: string): Promise<void> => {
    await documentsApi.delete(`/${uuid}`);
  },

  /**
   * Download document
   * GET /documents/:uuid/download
   */
  downloadDocument: async (uuid: string): Promise<Blob> => {
    const response = await documentsApi.get<Blob>(`/${uuid}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Get document preview URL
   * Retourne l'URL pour prévisualiser ou obtenir la miniature d'un document
   */
  getDocumentPreviewUrl: (
    uuid: string,
    type: 'thumbnail' | 'preview' = 'preview'
  ): string => {
    const baseUrl = APP_CONFIG.api.baseUrl;
    if (type === 'thumbnail') {
      return `${baseUrl}/documents/${uuid}/thumbnail`;
    }
    return `${baseUrl}/documents/${uuid}/download`;
  },

  /**
   * Update document metadata
   * PATCH /documents/:uuid
   */
  updateDocument: async (
    uuid: string,
    updates: Partial<
      Pick<Document, 'name' | 'description' | 'category' | 'tags'>
    >
  ): Promise<Document> => {
    const response = await documentsApi.patch<{ data: Document }>(
      `/${uuid}`,
      updates
    );
    // Le backend enveloppe la réponse dans { data: {...}, meta: {...} }
    return response.data.data;
  },

  /**
   * Create text document
   * POST /documents/text
   */
  createTextDocument: async (data: TextDocumentCreate): Promise<Document> => {
    const response = await documentsApi.post<{ data: Document }>('/text', data);
    // Le backend enveloppe la réponse dans { data: {...}, meta: {...} }
    return response.data.data;
  },

  /**
   * Create credential document
   * POST /documents/credentials
   */
  createCredentialDocument: async (
    data: CredentialDocumentCreate
  ): Promise<Document> => {
    const response = await documentsApi.post<{ data: Document }>(
      '/credentials',
      data
    );
    // Le backend enveloppe la réponse dans { data: {...}, meta: {...} }
    return response.data.data;
  },

  /**
   * Export filtered documents as ZIP
   * POST /documents/export-zip
   */
  exportDocumentsZip: async (params: DocumentsQueryParams): Promise<Blob> => {
    const response = await documentsApi.post<Blob>('/export-zip', null, {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Decrypt credential document fields
   * POST /documents/:uuid/credentials/decrypt
   */
  decryptDocumentCredentials: async (
    uuid: string,
    password: string
  ): Promise<DocumentCredentialsResponse> => {
    const response = await documentsApi.post<{
      data: DocumentCredentialsResponse;
    }>(`/${uuid}/credentials/decrypt`, { password });
    return response.data.data;
  },
};
